import { downloadFile } from '#utils/download';
import { Button, Message } from '@bedrock/components';
import { SignOut } from '@bedrock/icons-react';
import React from 'react';
import {
  biddingExportAllProposals,
  demandCreateExportDemandPlanTask,
  demandExportCooperateData,
  demandExportExportAllProposals,
  demandExportExportScoreDetail,
  ExportTaskResultDto,
  myWorkspaceCreateExportAllDemandTask,
  myWorkspaceCreateExportAllTrumpetTask,
  myWorkspaceCreateExportTodoDemandTask,
  myWorkspaceCreateExportTodoTrumpetTask,
  myWorkspaceGetExportDemandTaskResult,
  outsourceDemandSourceCreateExportDemandSourceKolTask,
  outsourceWorkbenchesGetExportDemandTaskResult,
  workbenchesExportDemand,
  v2BiddingScoreExportsExportScoreDetail,
  v2TaskQuery, outsourceContentMarketQueryExportMonitorLinks, contentMarketQueryExportMonitorLinks,
  outsourceV2TaskQuery,
} from '#services/api';
import { ButtonSize } from '@bedrock/components/es/Button/Button';
import { ButtonProps } from '@bedrock/components/lib/Button';
import { buildCacheKey, clearCache, readCache, writeCache } from './cacheUtils';
import { $sType } from '@mpc/common/src/utils';

const EXPORT_FILE_KEY_MAP = {
  // 我的工作台常规采购全部列表
  DEMANDS_EXPORT_DEMAND: {
    taskApi: myWorkspaceCreateExportAllDemandTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 我的工作台常规采购待办列表
  DEMANDS_EXPORT_TODO_DEMAND: {
    taskApi: myWorkspaceCreateExportTodoDemandTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 常规采购列表
  DEMANDS_PLANNER_EXPORT: {
    taskApi: workbenchesExportDemand,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 我的工作台小喇叭待办列表
  TRUMPET_EXPORT_TODO_DEMAND: {
    taskApi: myWorkspaceCreateExportTodoTrumpetTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 我的工作台小喇叭全部列表
  TRUMPET_EXPORT_DEMAND: {
    taskApi: myWorkspaceCreateExportAllTrumpetTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 策划单报价信息
  PLAN_ORDER_QUOTE_INFO: {
    taskApi: demandCreateExportDemandPlanTask,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 批量提报达人
  BATCH_REPORTING_EXPERT: {
    taskApi: outsourceDemandSourceCreateExportDemandSourceKolTask,
    taskResultApi: outsourceWorkbenchesGetExportDemandTaskResult,
  },
  // 需求单-合作内容-导出
  DEMANDS_EXPORT_COOPERATE_DATA: {
    taskApi: demandExportCooperateData,
    taskResultApi: myWorkspaceGetExportDemandTaskResult,
  },
  // 常规采购询报价明细 - 导出全部方案
  DEMAND_EXPORT_ALL_PROPOSALS: {
    taskApi: demandExportExportAllProposals,
    taskResultApi: v2TaskQuery,
  },
  // 询比价评分明细 - 导出（整单全量：覆盖全部合作内容 × 全部供应商，与所在 Tab 无关）
  DEMAND_SCORE_EXPORT: {
    taskApi: demandExportExportScoreDetail,
    taskResultApi: v2TaskQuery,
  },
  // 比选供应商比选方案及评分 - 导出全部方案
  BIDDING_EXPORT_ALL_PROPOSALS: {
    taskApi: biddingExportAllProposals,
    taskResultApi: v2TaskQuery,
  },
  // 比选评分明细 - 导出（**当前评分轮 + 当前角色**；创建任务时须随请求带上页面当前填写值，
  // 见 getExportParams —— 评分在提交前不落库，纯读库会导出空表。仅线上版 `'1.0'` 可用）
  BIDDING_SCORE_EXPORT: {
    taskApi: v2BiddingScoreExportsExportScoreDetail,
    taskResultApi: v2TaskQuery,
  },

  // 检测链接excel导出
  MONITOR_LINKS_EXPORT: {
    taskApi: contentMarketQueryExportMonitorLinks,
    outsourceTaskApi: outsourceContentMarketQueryExportMonitorLinks,
    taskResultApi: v2TaskQuery,
    outsourceTaskResultApi: outsourceV2TaskQuery,
  }
};

interface IProps {
  exportKey: keyof typeof EXPORT_FILE_KEY_MAP;
  params: any;
  intervalTime?: number;
  buttonSize?: ButtonSize;
  buttonProps?: ButtonProps;
  buttonText?: string;
  enableCache?: boolean;
  cacheComponent?: string;
  getUserId?: () => string;
  cacheExpiry?: number;
  /**
   * 创建导出任务**之前**求值本次请求参数，与静态 {@link params} 合并（同名字段以本回调为准）。
   *
   * 用于「导出内容 = 页面当前填写内容」的场景（#50110）：这些内容在提交前不落库，
   * 只能在**点击时刻**从页面取值随创建任务的请求一起提交；而静态 `params` 在渲染时就固定了，
   * 拿不到那一刻的编辑结果（异步任务在创建时就取好数据快照，晚于它取值来不及）。
   *
   * 回调抛异常即中止本次导出、不创建任务 —— 取不到当前内容时导出的是空表，比不导出更误导。
   */
  getExportParams?: () => Promise<Record<string, any>> | Record<string, any>;
}

interface PollingOptions {
  taskApi: () => Promise<any>;
  taskResultApi: (p: { task_id: string }) => Promise<any>;
  intervalTime: number;
  existingTaskId?: string;
  onTaskCreated?: (taskId: string) => void;
  onSuccess?: (data: ExportTaskResultDto) => void;
  onFailure?: () => void;
}

const exportFilePolling = async ({
  taskApi,
  taskResultApi,
  intervalTime,
  existingTaskId,
  onTaskCreated,
  onSuccess,
  onFailure,
}: PollingOptions) => {
  return new Promise<void>(async (resolve, reject) => {
    try {
      const taskId = existingTaskId ?? (await taskApi())?.data;
      onTaskCreated?.(taskId);
      const getExportResult = async () => {
        const resResult = await taskResultApi({ task_id: taskId })
          .catch(err => {
            onFailure?.();
            reject();
            return Promise.reject(err);
          });
        if (resResult?.data?.status === 1) {
          setTimeout(getExportResult, intervalTime);
        } else if (resResult?.data?.status === 2) {
          onFailure?.();
          Message.error('导出失败');
          reject();
        } else if (resResult?.data?.status === 3) {
          onSuccess?.(resResult.data);
          resResult?.data?.download_url && downloadFile(resResult.data.download_url);
          resolve();
        } else {
          onFailure?.();
          reject();
        }
      };
      setTimeout(getExportResult, intervalTime);
    } catch (error) {
      onFailure?.();
      reject();
    }
  });
};

const ExportFilePollingBtn: React.FC<IProps> = ({
  exportKey,
  params = {},
  intervalTime = 500,
  buttonSize = 'medium',
  buttonProps = {},
  buttonText,
  enableCache = false,
  cacheComponent,
  getUserId,
  cacheExpiry = 86400000,
  getExportParams,
}) => {
  const [loading, setLoading] = React.useState(false);
  const config = EXPORT_FILE_KEY_MAP[exportKey];
  const isOuter = $sType !== 'inner';
  const taskApi = (isOuter && config?.outsourceTaskApi) ? config.outsourceTaskApi : config?.taskApi;
  const taskResultApi = (isOuter && config?.outsourceTaskResultApi) ? config.outsourceTaskResultApi : config?.taskResultApi;

  const handleExport = async (e) => {
    e && e.stopPropagation();

    // 参数在**点击时刻**才求值：导出内容可能是页面上尚未落库的编辑结果，
    // 渲染期就固定的静态 params 拿不到它（#50110）
    let exportParams = params;
    if (getExportParams) {
      try {
        exportParams = {...params, ...(await getExportParams())};
      } catch (error) {
        // 取不到当前内容就不创建任务：导出空表比不导出更误导
        console.error('获取导出参数失败，已取消导出', error);
        Message.error('导出失败，请重试');
        return;
      }
    }

    let existingTaskId: string | undefined;

    if (enableCache && cacheComponent) {
      const userId = getUserId?.() ?? 'anonymous';
      const cacheKey = buildCacheKey(cacheComponent, userId, exportParams);
      const now = Date.now();
      const cached = readCache<ExportTaskResultDto>(cacheKey);

      if (cached && cached.expires_at > now) {
        if (cached.status === 'running') {
          existingTaskId = cached.task_id;
        } else {
          clearCache(cacheKey);
        }
      } else if (cached) {
        clearCache(cacheKey);
      }
    }

    setLoading(true);

    const cacheCallbacks = (enableCache && cacheComponent) ? (() => {
      const userId = getUserId?.() ?? 'anonymous';
      const key = buildCacheKey(cacheComponent, userId, exportParams);
      const expiry = cacheExpiry ?? 86400000;
      return {
        onTaskCreated: (taskId: string) => {
          writeCache<ExportTaskResultDto>(key, {
            task_id: taskId,
            status: 'running',
            created_at: Date.now(),
            expires_at: Date.now() + expiry,
          });
        },
        onSuccess: () => clearCache(key),
        onFailure: () => clearCache(key),
      };
    })() : {};

    exportFilePolling({
      taskApi: () => taskApi(exportParams),
      taskResultApi,
      intervalTime,
      existingTaskId,
      ...cacheCallbacks,
    }).finally(() => {
      setLoading(false);
    });
  };

  return (
    <Button size={buttonSize} icon={<SignOut />} type='checked-neutral' onClick={handleExport} loading={loading} {...buttonProps}>
      {buttonText || '导出'}
    </Button>
  );
};

export default ExportFilePollingBtn;
